import { useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "react-redux";
import { store } from "@common/store/store";
import ErrorBoundary from "@jkast90/kast-common/error-boundary";
import { statusOf } from "@common/api";
import { createQueryClient, useCacheScopedToUser } from "@jkast90/kast-common/query-client";
import { useAppSelector } from "@common/store/hooks";
import TooltipHost from "@common/components/TooltipHost";
import { logCrash, PageBoundary, SiteLayout } from "@common/components/SiteLayout";
import NavRecorder from "@common/components/NavRecorder";
import { redirectToLogin } from "@common/store/slices/authSlice";
import { adminModeOn } from "@common/adminMode";
import HashTokenHandler from "./auth/components/HashTokenHandler";
import DesktopSignIn from "./desktop/DesktopSignIn";
import DesktopPlaytime from "./desktop/DesktopPlaytime";
import AdoptedSaves from "./library/AdoptedSaves";
import { isDesktop } from "./desktop/desktop";
import { useOfflineSync } from "./emulator/offlineSync";
import AboutPage from "./pages/AboutPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import BugReportPage, { BugReport } from "./pages/BugReportPage";
import HomePage from "./pages/HomePage";
import LibraryPage from "./pages/LibraryPage";
import PlayPage from "./pages/PlayPage";

function OfflineSync() {
  useOfflineSync();
  return null;
}

// No subscribeToAuthErrors: the app has no 401 signal of its own. A query's
// 401 clears the cache, and so does the account changing (below).
const queryClient = createQueryClient({ statusOf });

// Not every change of account is a page load (a refresh rejected mid-session
// is not), so the cache is cleared when the account changes, whatever changed it.
function CacheScope() {
  useCacheScopedToUser(queryClient, useAppSelector((s) => s.auth.userSub));
  return null;
}

// Report This on the last-resort crash screen: the crash was outside the
// router or the store's provider, so the form shows in place of the app,
// reading the store directly. A page load would lose the logs.
function CrashReport({ crashNote }: { crashNote: string }) {
  const { isAuthenticated, userSub, userEmail, userGroups } = store.getState().auth;
  return (
    <ErrorBoundary onError={logCrash} buttonClassName="btn" primaryButtonClassName="btn primary">
      <div className="app about">
        <main>
          <BugReport
            user={isAuthenticated ? { id: userSub, email: userEmail } : null}
            admin={isAuthenticated && adminModeOn(userGroups)}
            onSignIn={() => store.dispatch(redirectToLogin())}
            initialDescription={crashNote}
          />
        </main>
      </div>
    </ErrorBoundary>
  );
}

export default function App() {
  const [crashNote, setCrashNote] = useState<string | null>(null);
  if (crashNote !== null) return <CrashReport crashNote={crashNote} />;
  return (
    // The last resort, for a crash outside a page (the providers, the top
    // bar, the sign-in dialogs). Pages have their own, below.
    <ErrorBoundary onError={logCrash} onReport={setCrashNote} buttonClassName="btn" primaryButtonClassName="btn primary">
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <CacheScope />
          <BrowserRouter>
            <NavRecorder />
            <HashTokenHandler />
            <DesktopSignIn />
            {isDesktop && <DesktopPlaytime />}
            <OfflineSync />
            <TooltipHost />
            <AdoptedSaves />
            <Routes>
              {/* The player has its own header. Leaving it is a full page
                  load, so Reload is the way out of a crash there, but
                  Report This moves in-app, keeping the logs. */}
              <Route
                path="/play"
                element={
                  <PageBoundary>
                    <PlayPage />
                  </PageBoundary>
                }
              />
              <Route element={<SiteLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/bug-report" element={<BugReportPage />} />
              </Route>
              {/* Games & players became part of the library. */}
              <Route path="/dashboard" element={<Navigate to="/library" replace />} />
              {/* My games and My saves became the library. */}
              <Route path="/games" element={<Navigate to="/library" replace />} />
              <Route path="/saves" element={<Navigate to="/library" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </Provider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
